import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { searchSuppliers } from '@/api/suppliers';

/**
 * Searchable Vendor picker backed by the OnsiteCore supplier master
 * (oscar.vSupplier). Shows the supplier name in the field; commits both the
 * name (for display / notify) and the durable SupplierId (for Ledger) via
 * onChange. Free-typed text is never committed — only a picked supplier is, so
 * Vendor always matches a real creditor. A legacy contract with a name but no
 * id shows the name and can be re-picked to attach the id.
 */
export function SupplierPicker({ valueName, valueId, onChange, error, inputClassName }: {
  valueName: string;
  valueId: number | null;
  onChange: (name: string, id: number | null) => void;
  error?: string;
  inputClassName?: string;
}) {
  const [query, setQuery] = useState(valueName);
  const [debounced, setDebounced] = useState(valueName);
  const [open, setOpen] = useState(false);

  // Keep the field in sync when the form resets (e.g. edit data loads).
  useEffect(() => { setQuery(valueName); }, [valueName]);
  // Debounce the search term so we don't hit the API on every keystroke.
  useEffect(() => {
    const t = setTimeout(() => setDebounced(query.trim()), 250);
    return () => clearTimeout(t);
  }, [query]);

  const results = useQuery({
    queryKey: ['suppliers', debounced],
    queryFn: ({ signal }) => searchSuppliers(debounced, signal),
    enabled: open && debounced.length >= 2,
    retry: false,
  });

  const commit = (name: string, id: number | null) => {
    onChange(name, id);
    setQuery(name);
    setOpen(false);
  };
  const revert = () => setQuery(valueName);

  const rows = results.data ?? [];
  const showMenu = open && debounced.length >= 2;

  return (
    <div className="relative">
      <input
        value={query}
        onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        onBlur={() => { setOpen(false); revert(); }}
        placeholder="Search suppliers…"
        aria-label="Vendor"
        autoComplete="off"
        role="combobox"
        aria-expanded={showMenu}
        className={inputClassName}
      />
      {error && <span className="mt-1 block font-normal text-red">{error}</span>}
      {!error && !open && valueName.trim() !== '' && valueId === null && (
        <span className="mt-1 block font-normal text-darkGrey/60">Not linked to a supplier record — pick from the list to link it.</span>
      )}
      {showMenu && (
        <ul className="absolute z-10 mt-1 max-h-56 w-full overflow-y-auto rounded border border-border bg-white text-sm shadow-md">
          {results.isLoading && <li className="px-3 py-2 text-darkGrey/60">Searching…</li>}
          {results.isError && <li className="px-3 py-2 text-red">Supplier lookup unavailable.</li>}
          {!results.isLoading && !results.isError && rows.length === 0 && (
            <li className="px-3 py-2 text-darkGrey/60">No suppliers match “{debounced}”.</li>
          )}
          {rows.map((s) => (
            <li key={s.SupplierId}>
              <button
                type="button"
                // Prevent the input from blurring (which would close the menu and
                // revert) before the click registers — the standard combobox trick.
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => commit(s.Name, s.SupplierId)}
                className="block w-full px-3 py-1.5 text-left hover:bg-bg"
              >
                <span className="font-medium text-navy">{s.Name}</span>
                {s.CreditorCode && <span className="ml-1.5 text-xs text-darkGrey/50">{s.CreditorCode}</span>}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
